import React from "react";
import "./ConfirmTransferModal.scoped.css";

/**
 * ConfirmTransferModal
 *
 * Displays a summary of the transfer details for the user to review
 * before the transfer is submitted. Controlled by the `show_confirmation_page`
 * boolean in the FI's branding config.
 *
 * Props:
 *   transferSummary - object containing:
 *     recipient        - display string for the recipient
 *     amount           - dollar amount string (e.g. "25.00")
 *     note             - memo / note string
 *     fromLabel        - label for the source (e.g. "Checking ••••1234" or card nickname)
 *     isScheduled      - bool, true for S+R transfers
 *     scheduledDate    - optional formatted date string for scheduled transfers
 *     repeatFrequency  - optional repeat frequency label (e.g. "Weekly", "Monthly", "Never")
 *   onConfirm          - callback when user confirms
 *   onCancel           - callback when user cancels / goes back
 */
const ConfirmTransferModal = ({ transferSummary, onConfirm, onCancel }) => {
  const {
    recipient,
    amount,
    note,
    fromLabel,
    isScheduled,
    scheduledDate,
    repeatFrequency,
  } = transferSummary;

  return (
    <div
      className="confirm-transfer-modal-overlay np-dialog-backdrop"
      onClick={onCancel}
      data-cy="confirm-transfer-overlay"
    >
      <div
        className="confirm-transfer-modal np-dialog"
        onClick={(e) => e.stopPropagation()}
        data-cy="confirm-transfer-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-transfer-title"
      >
        {/* Header */}
        <div className="confirm-transfer-header">
          <div className="confirm-transfer-title" id="confirm-transfer-title">
            {isScheduled ? "Confirm scheduled transfer" : "Confirm transfer"}
          </div>
          <div className="confirm-transfer-amount" data-cy="confirm-modal-amount">
            ${amount}
          </div>
        </div>

        {/* Details */}
        <div className="np-card-sunken confirm-transfer-content">
          <div className="np-detail-row">
            <span className="np-detail-label">To</span>
            <span
              className="np-detail-value np-detail-value-medium"
              data-cy="confirm-modal-recipient"
            >
              {recipient}
            </span>
          </div>

          <div className="np-detail-row">
            <span className="np-detail-label">From</span>
            <span
              className="np-detail-value np-detail-value-medium"
              data-cy="confirm-modal-from"
            >
              {fromLabel}
            </span>
          </div>

          {note ? (
            <div className="np-detail-row">
              <span className="np-detail-label">Note</span>
              <span
                className="np-detail-value np-detail-value-medium"
                data-cy="confirm-modal-note"
              >
                {note}
              </span>
            </div>
          ) : null}

          {isScheduled && scheduledDate ? (
            <div className="np-detail-row">
              <span className="np-detail-label">Scheduled date</span>
              <span
                className="np-detail-value np-detail-value-medium"
                data-cy="confirm-modal-date"
              >
                {scheduledDate}
              </span>
            </div>
          ) : null}

          {isScheduled && repeatFrequency && repeatFrequency !== "NEVER" ? (
            <div className="np-detail-row">
              <span className="np-detail-label">Repeats</span>
              <span
                className="np-detail-value np-detail-value-medium"
                data-cy="confirm-modal-recurrence"
              >
                {repeatFrequency}
              </span>
            </div>
          ) : null}
        </div>

        {/* Action buttons */}
        <div className="np-actions confirm-transfer-footer">
          <button type="button" onClick={onConfirm} data-cy="confirm-modal-submit">
            {isScheduled ? "Confirm Scheduled Transfer" : "Confirm & send"}
          </button>
          <button
            type="button"
            className="np-btn-secondary confirm-modal-back-btn"
            onClick={onCancel}
            data-cy="confirm-modal-cancel"
          >
            Go back
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmTransferModal;
